import { HTTPError } from "ky";

export type TRetryOptions = {
  maxAttempts?: number;
  baseDelayMs?: number;
};

/**
 * Returns true if the error is retryable (transient).
 * - Network errors (TypeError from fetch)
 * - HTTP 429 (rate limit)
 * - HTTP 5xx (server errors)
 */
function isRetryableError(error: unknown): boolean {
  // Network errors (fetch failed, timeout, etc.)
  if (error instanceof TypeError) {
    return true;
  }
  if (error instanceof HTTPError) {
    const status = error.response?.status;
    if (status == null) return true;
    if (status === 429) return true;
    if (status >= 500 && status < 600) return true;
    return false;
  }
  return false;
}

/**
 * Wraps an async function with retry logic.
 * Retries only on transient errors: network failures, 429, 5xx.
 * Uses exponential backoff between attempts.
 *
 * @param fn - Async function to execute (may throw)
 * @param options - maxAttempts (default 3), baseDelayMs (default 1000)
 * @returns The result of fn, or rethrows after exhausting retries
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options?: TRetryOptions
): Promise<T> {
  const maxAttempts = options?.maxAttempts ?? 3;
  const baseDelayMs = options?.baseDelayMs ?? 1000;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxAttempts || !isRetryableError(error)) {
        throw error;
      }
      const delayMs = baseDelayMs * Math.pow(2, attempt - 1);
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  throw new Error("Unreachable");
}
